// Dati della Monthly Tracking View V2: progetti gestiti, ore per progetto/giorno, dettaglio persone.
import { API_BASE_URL, API_ENDPOINTS } from '../config/api.config';
import { fetchProjectCalendarMembers, resolveDisplayName } from './absenceCalendarExport';

async function request(url, options) {
  const res = await fetch(url, options);
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }
  if (!res.ok) throw new Error(body?.error || body?.message || `Request failed (${res.status})`);
  return body || {};
}

const dayOf = (workDate) => parseInt(String(workDate).slice(8, 10), 10);

/**
 * Progetti visibili all'utente, con la stessa regola della vista classica:
 * - Delivery Manager: tutti i progetti
 * - Admin / Service Manager / JR SM / Team Leader: progetti con ruolo 1,2,3
 * - altri ruoli: progetti dell'utente
 */
export async function fetchVisibleProjects({ seesAllProjects, seesManagedProjects, userId }) {
  if (seesAllProjects) {
    const data = await request(API_ENDPOINTS.PROJECTS);
    return (data.projects || []).map((p) => ({ id: p.id, name: p.name, customer: p.customer || null }));
  }
  if (seesManagedProjects) {
    const data = await request(`${API_ENDPOINTS.PROJECT_USERS}?user_id=${userId}&role_ids=1,2,3`);
    return (data.user_projects_with_references || []).map((item) => ({
      id: item.project.id,
      name: item.project.name,
      customer: item.customer || null,
      project_role: item.project_role,
    }));
  }
  const data = await request(`${API_ENDPOINTS.PROJECTS}?user_id=${userId}`);
  return (data.projects || []).map((p) => ({ id: p.id, name: p.name, customer: p.customer || null }));
}

/** Minuti per progetto e giorno: { [projectId]: { [day]: minutes } }. */
export async function fetchProjectDailyMinutes(yearMonth, projectIds, activityIds) {
  if (!projectIds.length) return {};
  const body = { year_month: yearMonth, project_ids: projectIds };
  if (activityIds.length) body.activity_ids = activityIds;
  const data = await request(`${API_BASE_URL}/monthly_project_effort_summary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const out = {};
  (data.data || []).forEach((row) => {
    const pid = row.project_id;
    if (!out[pid]) out[pid] = {};
    const day = dayOf(row.work_date);
    out[pid][day] = (out[pid][day] || 0) + (Number(row.total_effort) || 0);
  });
  return out;
}

/**
 * Persone del progetto nel mese (assegnate o con ore), con team e minuti per giorno.
 * @returns {Promise<{ people: Array<{ key, userId, name, team, days, total }>, projectUsers, teamMembershipByUsername }>}
 */
export async function fetchProjectPeople(projectId, year, month, activityIds) {
  const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
  const params = new URLSearchParams({ project_id: String(projectId), year_month: yearMonth });
  if (activityIds.length) params.set('activity_ids', activityIds.join(','));

  const [summary, members] = await Promise.all([
    request(`${API_BASE_URL}/monthly_worklog_summary_user_project?${params}`),
    fetchProjectCalendarMembers(projectId, year, month),
  ]);
  const { projectUsers, teamMembershipByUsername } = members;

  const byUser = new Map();
  const ensure = (userId, name) => {
    const key = userId != null ? `id:${userId}` : `name:${name}`;
    if (!byUser.has(key)) {
      byUser.set(key, { key, userId: userId != null ? String(userId) : null, name, team: teamMembershipByUsername[name] || null, days: {}, total: 0 });
    }
    return byUser.get(key);
  };

  projectUsers.forEach((u) => ensure(u.id, resolveDisplayName(u)));
  (summary.data || []).forEach((row) => {
    const person = ensure(row.user_id, row.username);
    const minutes = Number(row.actual_effort ?? row.total_effort) || 0;
    const day = dayOf(row.work_date);
    person.days[day] = (person.days[day] || 0) + minutes;
    person.total += minutes;
  });

  const people = [...byUser.values()].sort((a, b) => {
    const ta = a.team || '￿';
    const tb = b.team || '￿';
    return ta.localeCompare(tb) || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
  return { people, projectUsers, teamMembershipByUsername };
}
